import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Grid,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  useTheme,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { userInfoState } from "../global-state";
import useApiCall from "../common/api-call";
import theme from "../theme";
import { useToast } from "../common/toast";
import { listAutomations } from "../automation/automation-service";
import dayjs from "dayjs";
import { customFormat } from "../common/custom-format";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import ErrorIcon from "@mui/icons-material/Error";

const TaskFlowTempView = () => {
  const [automations, setAutomations] = useState([]);
  const [filteredAutomations, setFilteredAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [userInfo] = useRecoilState(userInfoState);
  const { apiCall } = useApiCall();
  const { errorToast } = useToast();
  const history = useHistory();
  const [runningFilter, setRunningFilter] = useState("all");
  const muiTheme = useTheme();

  const handleListTaskFlows = async () => {
    setLoading(true);
    try {
      const resp = await listAutomations(
        apiCall,
        userInfo.activeScope.id,
        "temp",
        "task_flow"
      );
      if (resp && Array.isArray(resp.automations)) {
        const taskFlowAutomations = resp["automations"];

        const filtered = taskFlowAutomations
          .filter((automation) => {
            if (!searchTerm) return true;
            const searchTerms = searchTerm
              .toLowerCase()
              .split(" ")
              .filter((term) => term.length > 0);
            const searchableText = automation.name.toLowerCase();
            return searchTerms.every((term) => searchableText.includes(term));
          })
          .sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)));

        setAutomations(filtered);
        setFilteredAutomations(filtered);
      } else {
        setAutomations([]);
        setFilteredAutomations([]);
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
      errorToast("Failed to fetch automations");
      setAutomations([]);
      setFilteredAutomations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleListTaskFlows();

    // Update current time every minute
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // When searching or changing running filter, maintain the timestamp sorting
    const filtered = automations
      .filter((automation) =>
        automation.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter((automation) => {
        if (runningFilter === "all") return true;
        if (runningFilter === "running") return automation.running;
        if (runningFilter === "not-running") return !automation.running;
        return true;
      })
      .sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)));

    setFilteredAutomations(filtered);
  }, [searchTerm, automations, runningFilter]);

  const handleRefresh = () => {
    handleListTaskFlows();
  };

  const openOverview = (data) => {
    history.push(`/automation/overview?id=${data.id}`);
  };

  // Function to truncate name if it's more than 2 lines
  const truncateName = (name) => {
    // Create a temporary element to measure line height
    const temp = document.createElement("div");
    temp.style.visibility = "hidden";
    temp.style.position = "absolute";
    temp.style.whiteSpace = "normal";
    temp.style.width = "200px"; // Match card width
    temp.style.font = "inherit";
    temp.textContent = name;
    document.body.appendChild(temp);

    // Get line height and count lines
    const lineHeight = parseFloat(window.getComputedStyle(temp).lineHeight);
    const height = temp.offsetHeight;
    const lines = Math.round(height / lineHeight);

    document.body.removeChild(temp);

    // If more than 2 lines, truncate
    if (lines > 2) {
      return name.slice(0, 80) + "...";
    }
    return name;
  };

  return (
    <Box
      sx={{
        padding: 2,
        backgroundColor: muiTheme.palette.background.default,
      }}
    >
      <Card
        sx={{
          mb: 1,
          backgroundColor: muiTheme.palette.background.paper,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              placeholder="Filter by name"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                sx: {
                  backgroundColor: "background.input",
                  height: "40px",
                  "& .MuiOutlinedInput-input": {
                    padding: "6px 10px",
                    height: "40px",
                  },
                  borderRadius: "4px",
                },
              }}
            />
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                height: "40px",
                width: "40px",
                backgroundColor: muiTheme.palette.background.default,
                borderRadius: "4px",
                padding: "6px",
                "&:hover": {
                  backgroundColor: muiTheme.palette.action.hover,
                },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  color: loading ? "text.disabled" : "text.primary",
                  animation: loading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Box>

          <RadioGroup
            row
            value={runningFilter}
            onChange={(e) => setRunningFilter(e.target.value)}
            sx={{
              "& .MuiFormControlLabel-root": {
                mr: 2,
                ml: 0,
              },
              "& .MuiRadio-root": {
                padding: "4px",
              },
            }}
          >
            <FormControlLabel
              value="all"
              control={<Radio size="small" />}
              label="All"
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "0.875rem",
                  color: muiTheme.palette.text.primary,
                },
              }}
            />
            <FormControlLabel
              value="running"
              control={<Radio size="small" />}
              label="Running"
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "0.875rem",
                  color: muiTheme.palette.text.primary,
                },
              }}
            />
            <FormControlLabel
              value="not-running"
              control={<Radio size="small" />}
              label="Not Running"
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "0.875rem",
                  color: muiTheme.palette.text.primary,
                },
              }}
            />
          </RadioGroup>
        </Box>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={1}>
          {filteredAutomations.length > 0 ? (
            filteredAutomations.map((automation) => (
              <Grid item xs={12} sm={6} md={4} key={automation.id}>
                <Card
                  sx={{
                    height: "100%",
                    backgroundColor: muiTheme.palette.background.card,
                    boxShadow: "none",
                    border: `1px solid ${muiTheme.palette.divider}`,
                    borderRadius: 1,
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: muiTheme.palette.action.hover,
                    },
                  }}
                  variant="outlined"
                  onClick={() => openOverview(automation)}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      p: 1.5,
                      "&:last-child": {
                        paddingBottom: 1.5,
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: automation.running
                            ? muiTheme.palette.warning.main
                            : muiTheme.palette.success.main,
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{
                          flexGrow: 1,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          fontWeight: 500,
                          color: muiTheme.palette.text.primary,
                        }}
                      >
                        {truncateName(automation.name)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        textAlign: "left",
                        lineHeight: 1.2,
                        color: muiTheme.palette.text.secondary,
                      }}
                    >
                      {customFormat(dayjs(automation.timestamp), currentTime)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: muiTheme.palette.background.paper,
                  borderRadius: 2,
                  boxShadow: "none",
                  border: `1px solid ${muiTheme.palette.divider}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: muiTheme.palette.text.secondary,
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  No commands found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: muiTheme.palette.text.secondary,
                    textAlign: "center",
                  }}
                >
                  There are no commands to display for the selected filters.
                </Typography>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default TaskFlowTempView;
